import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box, Paper, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, Alert } from "@mui/material";
import { styled } from "@mui/material/styles";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import IncentiveCard from "../../Components/IncentiveCard";
import IncentivesErrorBoundary from "../../Components/IncentivesErrorBoundary";
import { incentivesApi } from "../../Components/incentivesApi";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useTheme } from "../../hooks/useTheme";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { createMuiThemeFromMode } from "../../theme/theme";
import styles from "../AllEnquiries.module.scss";
import dashboardStyles from "../dashboard/Dashboard.module.scss";

const DEBOUNCE_MS = 300;

const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "this_year", label: "This year" },
  { value: "custom", label: "Custom" },
];

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

function toYYYYMMDD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDateRangeForPreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  switch (preset) {
    case "today":
      return { date_from: toYYYYMMDD(today), date_to: toYYYYMMDD(today) };
    case "yesterday":
      return { date_from: toYYYYMMDD(yesterday), date_to: toYYYYMMDD(yesterday) };
    case "this_week": {
      const day = today.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const start = new Date(today);
      start.setDate(today.getDate() + mondayOffset);
      return { date_from: toYYYYMMDD(start), date_to: toYYYYMMDD(today) };
    }
    case "last_week": {
      const day = today.getDay();
      const mondayOffset = day === 0 ? -6 : 1 - day;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() + mondayOffset);
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(lastMonday.getDate() - 7);
      const lastSunday = new Date(lastMonday);
      lastSunday.setDate(lastSunday.getDate() + 6);
      return { date_from: toYYYYMMDD(lastMonday), date_to: toYYYYMMDD(lastSunday) };
    }
    case "this_month": {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { date_from: toYYYYMMDD(first), date_to: toYYYYMMDD(today) };
    }
    case "last_month": {
      const firstThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastMonthEnd = new Date(firstThisMonth);
      lastMonthEnd.setDate(0);
      const lastMonthStart = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      return { date_from: toYYYYMMDD(lastMonthStart), date_to: toYYYYMMDD(lastMonthEnd) };
    }
    case "this_year": {
      const jan1 = new Date(today.getFullYear(), 0, 1);
      return { date_from: toYYYYMMDD(jan1), date_to: toYYYYMMDD(today) };
    }
    default:
      return null;
  }
}

function defaultDateRange() {
  return getDateRangeForPreset("this_month") || { date_from: "", date_to: "" };
}

function IncentivesDashboardInner() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );

  const [datePreset, setDatePreset] = useState("this_month");
  const [filters, setFilters] = useState(() => defaultDateRange());
  const debouncedFilters = useDebouncedValue(filters, DEBOUNCE_MS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    if (!debouncedFilters.date_from || !debouncedFilters.date_to) {
      setData(null);
      setLoading(false);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    setError(null);
    try {
      const res = await incentivesApi.getSummary(debouncedFilters, abortRef.current.signal);
      setData(res);
    } catch (err) {
      if (err.name === "AbortError") return;
      setError(err.message || "Failed to load summary");
      setData(null);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [debouncedFilters]);

  useEffect(() => {
    fetchSummary();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchSummary]);

  const cardProps = useMemo(() => {
    if (!data) return [];
    return [
      {
        type: "bonus",
        title: "Bonus",
        dateFrom: debouncedFilters.date_from,
        dateTo: debouncedFilters.date_to,
        earned: data.bonus?.earned ?? 0,
        potential: data.bonus?.potential ?? 0,
        progress: data.bonus?.progress ?? 0,
        summaryInfo: `Completed: ${data.bonus?.completed ?? 0}, Pending: ${data.bonus?.pending ?? 0}`,
        variant: "success",
      },
      {
        type: "bounty",
        title: "Bounty",
        dateFrom: debouncedFilters.date_from,
        dateTo: debouncedFilters.date_to,
        earned: data.bounty?.earned ?? 0,
        potential: data.bounty?.potential ?? 0,
        progress: data.bounty?.progress ?? 0,
        summaryInfo: `Completed: ${data.bounty?.completed ?? 0}, Pending: ${data.bounty?.pending ?? 0}`,
        variant: "info",
      },
      {
        type: "commission",
        title: "Commission",
        dateFrom: debouncedFilters.date_from,
        dateTo: debouncedFilters.date_to,
        earned: data.commission?.earned ?? 0,
        potential: data.commission?.next_rate != null ? `Next rate: ${data.commission.next_rate}` : "—",
        progress: data.commission?.current_rate != null ? 50 : 0,
        summaryInfo:
          data.commission?.remaining_to_next != null
            ? `Remaining to next tier: ${data.commission.remaining_to_next}`
            : null,
        variant: "primary",
      },
      {
        type: "targets",
        title: "Targets",
        dateFrom: debouncedFilters.date_from,
        dateTo: debouncedFilters.date_to,
        earned: data.targets?.earned ?? 0,
        potential: data.targets?.potential ?? 0,
        progress: data.targets?.progress ?? 0,
        summaryInfo: `Completed: ${data.targets?.completed ?? 0}, Pending: ${data.targets?.pending ?? 0}`,
        variant: "warning",
      },
    ];
  }, [data, debouncedFilters.date_from, debouncedFilters.date_to]);

  return (
    <ThemeProvider theme={theme}>
      <Box className={`${styles.root} ${dashboardStyles.dashboardContent}`}>
        <Paper elevation={0} className={`${styles.contentPaper} ${dashboardStyles.contentPaper}`}>
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="h4" className={styles.pageTitle}>
              Counsellor Incentives Dashboard
            </Typography>
            <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
              View bonus, bounty, commission, and targets for the selected period.
            </Typography>
          </Box>

          <FilterPaper variant="outlined">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date range</InputLabel>
                  <Select
                    value={datePreset}
                    label="Date range"
                    onChange={(e) => {
                      const preset = e.target.value;
                      setDatePreset(preset);
                      const range = getDateRangeForPreset(preset);
                      if (range) setFilters(range);
                    }}
                  >
                    {DATE_PRESETS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {datePreset === "custom" && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="From"
                      type="date"
                      value={filters.date_from || ""}
                      onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="To"
                      type="date"
                      value={filters.date_to || ""}
                      onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </FilterPaper>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "action.hover",
                      minHeight: 200,
                    }}
                  >
                    <Skeleton height={24} width="40%" style={{ marginBottom: 12 }} />
                    <Skeleton height={14} width="50%" style={{ marginBottom: 4 }} />
                    <Skeleton height={20} width="60%" style={{ marginBottom: 12 }} />
                    <Skeleton height={14} width="50%" style={{ marginBottom: 4 }} />
                    <Skeleton height={20} width="70%" style={{ marginBottom: 12 }} />
                    <Skeleton height={8} sx={{ borderRadius: 1 }} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {!loading && data && (
            <Grid container spacing={2}>
              {cardProps.map((props) => (
                <Grid item xs={12} sm={6} key={props.type}>
                  <IncentiveCard {...props} />
                </Grid>
              ))}
            </Grid>
          )}

          {!loading && !data && !error && debouncedFilters.date_from && debouncedFilters.date_to && (
            <Typography color="text.secondary" variant="body2">
              No data for the selected period.
            </Typography>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}

export default function IncentivesDashboard() {
  return (
    <IncentivesErrorBoundary>
      <IncentivesDashboardInner />
    </IncentivesErrorBoundary>
  );
}
